// Browser-side x402 payment for human buyers using an injected EVM wallet
// (window.ethereum). Implements the x402 "exact" EVM scheme: the buyer signs a
// USDC EIP-3009 transferWithAuthorization (gasless), and the server's facilitator
// verifies + settles it on Base.
//
// NOTE: this signs a real on-chain authorization. Test against the live CDP
// facilitator + a funded wallet before relying on it in production.

interface Accept {
  network: string; maxAmountRequired: string; payTo: string; asset: string;
  extra?: { name?: string; version?: string };
}

const CHAIN_ID: Record<string, number> = { base: 8453, 'base-sepolia': 84532 };

function randomNonce(): string {
  const b = new Uint8Array(32);
  crypto.getRandomValues(b);
  return '0x' + Array.from(b).map((x) => x.toString(16).padStart(2, '0')).join('');
}

export function hasWallet(): boolean {
  return typeof (globalThis as any).ethereum !== 'undefined';
}

// Sign the payment authorization and return the base64 X-PAYMENT header value.
export async function buildXPaymentHeader(accept: Accept): Promise<string> {
  const eth = (globalThis as any).ethereum;
  if (!eth) throw new Error('No crypto wallet found. Install a Base-compatible wallet to pay with x402.');

  const accounts: string[] = await eth.request({ method: 'eth_requestAccounts' });
  const from = accounts?.[0];
  if (!from) throw new Error('No wallet account available.');

  const chainId = CHAIN_ID[accept.network] ?? 8453;
  const validAfter = '0';
  const validBefore = String(Math.floor(Date.now() / 1000) + 600); // 10 min window
  const nonce = randomNonce();

  const typedData = {
    types: {
      EIP712Domain: [
        { name: 'name', type: 'string' },
        { name: 'version', type: 'string' },
        { name: 'chainId', type: 'uint256' },
        { name: 'verifyingContract', type: 'address' },
      ],
      TransferWithAuthorization: [
        { name: 'from', type: 'address' },
        { name: 'to', type: 'address' },
        { name: 'value', type: 'uint256' },
        { name: 'validAfter', type: 'uint256' },
        { name: 'validBefore', type: 'uint256' },
        { name: 'nonce', type: 'bytes32' },
      ],
    },
    domain: {
      name: accept.extra?.name || 'USD Coin',
      version: accept.extra?.version || '2',
      chainId,
      verifyingContract: accept.asset,
    },
    primaryType: 'TransferWithAuthorization',
    message: { from, to: accept.payTo, value: accept.maxAmountRequired, validAfter, validBefore, nonce },
  };

  const signature: string = await eth.request({
    method: 'eth_signTypedData_v4',
    params: [from, JSON.stringify(typedData)],
  });

  const paymentPayload = {
    x402Version: 1,
    scheme: 'exact',
    network: accept.network,
    payload: {
      signature,
      authorization: { from, to: accept.payTo, value: accept.maxAmountRequired, validAfter, validBefore, nonce },
    },
  };

  return btoa(JSON.stringify(paymentPayload));
}
